import { FC, MouseEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { Box, Button, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { linkTypographyBoldSx } from '../../helpers/styles'
import { GameIconThumb, motivationAvailabilityColumn, trackedGameGenreColumn } from '../../helpers/trackedGameColumns'
import { useAllGamesListing } from '../../hooks/useAllGamesListing'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useGamesListingFilter } from '../../hooks/useGamesListingFilter'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { TrackedGame } from '../../types'
import { GamesListingFilterBar } from '../GamesListingFilterBar/GamesListingFilterBar'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const gameRowId = (row: TrackedGame) => row.id

/**
 * "Select Game for comparison" dialog opened from the Compare button on the
 * UA Insights game page. Lists every tracked game with:
 *  - clickable game name (navigates to the UA Insights game page),
 *  - genre,
 *  - per-source motivation availability chips (Game & Live Events / Creative Ads),
 *  - a Compare action button (navigates to the compare result view for that game).
 *
 * Column-width strategy mirrors `AllGamesDialog` (the working sibling that
 * also renders `TrackedGame` via `GRTable`): the flexible label columns
 * (Game, Genre) are left without an explicit `width` so they share leftover
 * space, while the trailing fixed-content columns (Motivations chips,
 * Compare button) carry explicit widths so the absolute-positioned virtual
 * body rows stay aligned with the header. The dialog uses `maxWidth="xl"`
 * to give enough room for all four columns at typical viewports.
 */
export const CompareGameDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, openGame, openCompareGameResult } = useUAInsightsNavigation()
  const { genreLabel } = useUATaxonomy()
  const open = params.dialog === 'compare-game'
  const { data, isLoading, isError, refetch } = useAllGamesListing(open)

  const games = useMemo(() => data?.games ?? [], [data?.games])
  const { search, genreFilter, availableGenres, filteredGames, handleSearchChange, handleGenreChange } = useGamesListingFilter(games)

  /**
   * Builds the UA Insights game detail URL, preserving the currently selected
   * network/month so cmd/ctrl-click (and middle-click) "open in new tab"
   * lands on the same filtered view as a regular click.
   */
  const buildGameHref = useCallback(
    (gameId: string) => {
      const qs = new URLSearchParams()
      if (params.networkId) qs.set('network', params.networkId)
      if (params.month) qs.set('month', params.month)
      const search = qs.toString() ? `?${qs.toString()}` : ''
      return `/ua-insights/game/${gameId}${search}`
    },
    [params.networkId, params.month]
  )

  /**
   * Navigates to the UA Insights game detail page and dismisses the compare
   * dialog. Cmd/Ctrl-click (and middle-click) is preserved so the native
   * "open in new tab" affordance still works on the underlying anchor.
   */
  const handleOpenGamePage = useCallback(
    (gameId: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.button === 1) return
      event.preventDefault()
      openGame(gameId)
    },
    [openGame]
  )

  const handleSelect = useCallback(
    (gameId: string) => {
      openCompareGameResult(gameId)
    },
    [openCompareGameResult]
  )

  const defaultColumns: GRTableColumn<TrackedGame, undefined>[] = useMemo(
    () => [
      {
        labelAccessor: t('col_game'),
        accessor: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GameIconThumb src={row.icon} alt="" />
            <Typography
              component="a"
              href={buildGameHref(row.id)}
              onClick={handleOpenGamePage(row.id)}
              variant="body2"
              sx={linkTypographyBoldSx}
              aria-label={t('open_game_details', { name: row.name }) as string}
            >
              {row.name}
            </Typography>
          </Box>
        ),
        sortAccessor: 'name',
        sortable: true,
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
      },
      trackedGameGenreColumn(t, genreLabel),
      motivationAvailabilityColumn(t),
      {
        // Action column. The header label is an explicitly-sized empty `Box`
        // (rather than an empty string) so the header `<th>` keeps the same
        // width as the body cell — without it, the header collapses to 0,
        // table-layout reflows the remaining columns to share the slack, and
        // the absolutely-positioned virtual body cell gets clipped on the
        // right by the container's `overflowX: hidden`. Width on
        // `headerCellProps.sx` reinforces that, since `column.width` is only
        // applied to body cells, not headers.
        labelAccessor: <Box sx={{ width: 160, height: 1 }} aria-hidden />,
        accessor: ({ row }) => (
          <Button variant="contained" color="primary" size="small" startIcon={<CompareArrowsIcon />} onClick={() => handleSelect(row.id)}>
            {t('compare')}
          </Button>
        ),
        sortable: false,
        width: 160,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { width: 160 } },
      },
    ],
    [t, genreLabel, handleSelect, handleOpenGamePage, buildGameHref]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const headerTitle = (
    <GamesListingFilterBar
      search={search}
      onSearchChange={handleSearchChange}
      genreFilter={genreFilter}
      onGenreChange={handleGenreChange}
      genres={availableGenres}
    />
  )

  const headerAction = (
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
      {t('games_count_section', { count: filteredGames.length })}
    </Typography>
  )

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={t('compare_game_dialog_title')}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      renderChildrenWhenEmpty
      loadingPlaceholder={<TableSkeleton rows={10} columns={5} />}
    >
      <SectionCard title={headerTitle} headerAction={headerAction} disableBodyPadding>
        <GRTable rows={filteredGames} columns={columns} onColumnsUpdated={setColumns} striped hoverable rowIdKey={gameRowId} noRowsLabel={t('no_games')} />
      </SectionCard>
    </UAInsightsDialog>
  )
}
